import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiPackage, FiRefreshCcw, FiDownloadCloud, FiEdit2, FiEye, FiX } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_PURCHASE_REPORT, GET_PURCHASE_ALL_DATA, GET_PURCHASE_DETAILS_BY_INVOICE_NO, BASE_URL, GET_SUPPLIER_MASTER } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import HighlightMetricsModal from './components/HighlightMetricsModal';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Internal bill id as "InvoiceNo/Sequence" (e.g. 1/2025). */
const formatInvoiceNoWithSequence = (row) => {
    const inv = row.InvoiceNo;
    const seq = row.Sequence;
    if (inv == null && seq == null) return '-';
    if (seq == null || seq === '') return inv != null ? String(inv) : '-';
    return `${inv}/${seq}`;
};

/** Map getPurchaseDetailsByInvoiceNo purchaseHd item to report table row shape. */
const mapPurchaseHdToRow = (item, invoiceNo) => ({
    InvoiceNo: invoiceNo,
    Sequence: item.Sequence,
    EditNo: item.EditNo ?? 0,
    PurchaseRefNo: item.PurchaseRefNo,
    PurchaseDate: item.InvoiceDate,
    SupplierName: item.SupplierName,
    NetAmount: item.TotalAmount,
    TaxAmount: item.TaxAmount,
    DiscountAmount: item.DiscountAmount,
    PaymentMode: item.PaymentMode,
    TotalDue: item.TotalDue,
});

/** True when the row belongs to the logged-in financial year (row.Sequence vs finac_year). */
const isPurchaseRowInCurrentFinYear = (row) => {
    const current = localStorage.getItem('finac_year');
    if (current == null || current === '') return false;
    if (row.Sequence == null || row.Sequence === '') return false;
    return String(row.Sequence) === String(current);
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      // Set both fromDate and toDate to today by default
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      if (filter.emptyDefault) {
        return { ...accumulator, [filter.id]: '' };
      }
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Custom Select Styles
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    fontSize: '14px',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    color: '#000000',
    '&:hover': {
      border: '1px solid #cbd5e1',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
    color: '#000000',
  }),
  input: (base) => ({
    ...base,
    color: '#000000',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#000000',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '40px',
  }),
  menu: (base) => ({
    ...base,
    fontSize: '14px',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 9999,
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? '#000000' : '#000000',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#4f46e5',
      color: '#000000',
    },
  }),
};

// Report configuration
const reportConfig = {
  filters: [
    {
      id: 'reportType',
      type: 'select',
      label: 'Report Type',
      options: [
        { label: 'Bill Wise', value: 'Bill' },
        { label: 'Supplier Wise', value: 'Supplier' },
        { label: 'Reference Number', value: 'Invoice' },
      ],
    },
    { id: 'invoiceNumber', type: 'text', label: 'Reference Number', invoiceOnly: true },
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
    {
      id: 'paymentMode',
      type: 'select',
      label: 'Payment Mode',
      options: [
        { label: 'All', value: 'all' },
        { label: 'Cash', value: 'cash' },
        { label: 'Credit', value: 'credit' },
        { label: 'Card', value: 'card' },
        { label: 'UPI', value: 'upi' },
      ],
    },
    {
      id: 'billDateType',
      type: 'select',
      label: 'Date By',
      billWiseOnly: true,
      emptyDefault: true,
      options: [
        { label: 'By Invoice Date', value: 'InvDate' },
        { label: 'By Entry Date', value: 'EntryDate' },
      ],
    },
  ],
  table: {
    columns: [
      { key: 'PurchaseRefNo', label: 'Invoice No' },
      { key: 'InvoiceNo', label: 'Ref No / Sequence' },
      { key: 'PurchaseDate', label: 'Invoice Date' },
      { key: 'SupplierName', label: 'Supplier Name' },
      { key: 'NetAmount', label: 'Grand Total' },
      { key: 'TaxAmount', label: 'Tax Amount' },
    ],
  },
};

export const PurchaseBillWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [purchaseData, setPurchaseData] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [purchaseDetails, setPurchaseDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [suppliersMaster, setSuppliersMaster] = useState([]);
    const token = localStorage.getItem('accessToken');
    const supplierSearchDebounceRef = useRef(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);

    // Initial load - fetch first 1000 suppliers
    useEffect(() => {
        const fetchSuppliers = async () => {
          try {
            const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
              headers: {
                'XApiKey': process.env.REACT_APP_API_KEY,
                'Authorization': `Bearer ${token}`,
                'FilterText': '',
              },
            });

            if (response.status === 200) {
              setSuppliersMaster(response.data?.data || []);
            }
          } catch (error) {
            console.error('Failed to fetch suppliers:', error);
          }
        };

        if (token) {
          fetchSuppliers();
        }
      }, [token]);

    // Load suppliers from API based on search text (for AsyncSelect)
    const loadSupplierOptions = (inputValue, callback) => {
        // Clear previous debounce timeout
        if (supplierSearchDebounceRef.current) {
          clearTimeout(supplierSearchDebounceRef.current);
        }

        // If input is less than 2 characters, return initial suppliers
        if (!inputValue || inputValue.length < 2) {
          const options = (suppliersMaster || []).map((supplier) => ({
            value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
            label: supplier.SupplierName ?? supplier.Name ?? '',
          }));
          const filteredOptions = inputValue
            ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
            : options;
          callback(filteredOptions);
          return;
        }

        // Debounce API call
        supplierSearchDebounceRef.current = setTimeout(async () => {
          try {
            const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
              headers: {
                'XApiKey': process.env.REACT_APP_API_KEY,
                'Authorization': `Bearer ${token}`,
                'FilterText': inputValue,
              },
            });

            if (response.status === 200) {
              const suppliers = response.data?.data || [];
              
              const options = suppliers.map((supplier) => ({
                value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
                label: supplier.SupplierName ?? supplier.Name ?? '',
                ...supplier,
              }));
              
              const filteredOptions = inputValue
                ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                : options;
              callback(filteredOptions);
            } else {
              callback([]);
            }
          } catch (error) {
            console.error('Failed to fetch suppliers:', error);
            callback([]);
          }
        }, 300); // 300ms debounce
    };

    // Get selected supplier option for AsyncSelect value prop
    const getSelectedSupplierOption = (supplierId) => {
        if (!supplierId) return null;
        
        // First, check if selectedSupplier exists and matches
        if (selectedSupplier) {
          const selectedValue = selectedSupplier.SupplierID ?? selectedSupplier.supplierID ?? selectedSupplier.Id ?? selectedSupplier.id ?? selectedSupplier.SupplierId ?? selectedSupplier.value;
          if (selectedValue === supplierId || String(selectedValue) === String(supplierId)) {
            return {
              value: selectedValue,
              label: selectedSupplier.SupplierName ?? selectedSupplier.Name ?? selectedSupplier.label ?? '',
            };
          }
        }
        
        // Fallback: search in suppliersMaster
        const supplier = suppliersMaster.find(s => {
          const sId = s.SupplierID ?? s.supplierID ?? s.Id ?? s.id ?? s.SupplierId;
          return sId === supplierId || String(sId) === String(supplierId);
        });
        
        if (supplier) {
          return {
            value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
            label: supplier.SupplierName ?? supplier.Name ?? '',
          };
        }
        
        return null;
    };

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
          if (supplierSearchDebounceRef.current) {
            clearTimeout(supplierSearchDebounceRef.current);
          }
        };
    }, []);

    // Handle supplier change
    const handleSupplierChange = (selectedOption) => {
        if (!selectedOption) {
          setSelectedSupplier(null);
          return;
        }
        
        const supplier = selectedOption;
        
        // Add to suppliersMaster if not already present
        if (supplier) {
          const supplierId = supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? supplier.value;
          const existingSupplier = suppliersMaster.find(s => {
            const sId = s.SupplierID ?? s.supplierID ?? s.Id ?? s.id ?? s.SupplierId;
            return sId === supplierId || String(sId) === String(supplierId);
          });
          
          if (!existingSupplier) {
            setSuppliersMaster(prev => [...prev, supplier]);
          }
        }
        
        setSelectedSupplier(selectedOption);
    };

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
        
        // Reset supplier selection when switching report type
        if (filterId === 'reportType') {
            setSelectedSupplier(null);
            setPurchaseData([]);
        }
    };

    const getReportTitle = () => {
        if (filters.reportType === 'Supplier') return 'Purchase Supplier Wise';
        if (filters.reportType === 'Invoice') return 'Purchase By Invoice Number';
        return 'Purchase Bill Wise';
    };

    const handleRunReport = () => {
        if (filters.reportType === 'Supplier' && !selectedSupplier) {
            toast.error('Please select a supplier', { autoClose: 2000 });
            return;
        }
        if (filters.reportType === 'Invoice') {
            const invoiceNo = String(filters.invoiceNumber ?? '').trim();
            if (!invoiceNo) {
                toast.error('Please enter an invoice number', { autoClose: 2000 });
                return;
            }
        }
        getPurchaseReport();
    };

    const handleExport = () => {
        if (purchaseData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        try {
            const excelData = purchaseData.map((row) => ({
                'Invoice No': row.PurchaseRefNo ?? '',
                'Ref No / Sequence': formatInvoiceNoWithSequence(row),
                'Invoice Date': row.PurchaseDate
                    ? new Date(row.PurchaseDate).toLocaleDateString('en-GB')
                    : '',
                'Supplier Name': row.SupplierName ?? '',
                'Grand Total': Number(parseFloat(row.NetAmount || 0).toFixed(2)),
                'Tax Amount': Number(parseFloat(row.TaxAmount || 0).toFixed(2)),
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [
                { wch: 18 },
                { wch: 16 },
                { wch: 14 },
                { wch: 28 },
                { wch: 14 },
                { wch: 12 },
            ];
            XLSX.utils.book_append_sheet(wb, ws, 'Purchase Bill Wise');

            const fromDate = filters.fromDate
                ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                : 'All';
            const toDate = filters.toDate
                ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                : 'All';
            const filename = `Purchase_Bill_Wise_${fromDate}_to_${toDate}.xlsx`;
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
        } catch (error) {
            console.error('Error exporting purchase report:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const handleEdit = (row) => {
        if (!isPurchaseRowInCurrentFinYear(row)) {
            toast.error('Editing is only allowed for bills in the current financial year.', { autoClose: 2000 });
            return;
        }

        // Store edit data in localStorage
        // Use InvoiceNo from the row, and default Sequence/EditNo if not available
        const editData = {
            invoiceNo: row.InvoiceNo,
            sequence: row.Sequence,
            editNo: row.EditNo !== undefined ? row.EditNo : 0,
        };
        
        if (!editData.invoiceNo || !editData.sequence) {
            toast.error('Invoice number not available', { autoClose: 1500 });
            return;
        }
        
        if (tabContext && tabContext.openTab) {
            tabContext.openTab(
                `purchaseedit/${encodeURIComponent(editData.invoiceNo)}/${encodeURIComponent(editData.sequence)}/${editData.editNo}`,
                `Purchase Edit - ${editData.invoiceNo}`
            );
        }
    };

    const handleViewDetails = async (row) => {
        const invoiceNo = row.InvoiceNo;
        const sequence = row.Sequence || 2024;
        const editNo = row.EditNo !== undefined ? row.EditNo : 0;

        if (!invoiceNo) {
            toast.error('Invoice number not available', { autoClose: 1500 });
            return;
        }

        setLoadingDetails(true);
        setShowDetailsModal(true);
        
        try {
            const response = await axios.get(`${BASE_URL}${GET_PURCHASE_ALL_DATA}`, {
                params: {
                    _sequence: sequence,
                    invoiceNo: invoiceNo,
                    editNo: editNo,
                    docType: 'Pharmacy',
                },
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.status === 200 && response.data?.isSucess) {
                setPurchaseDetails(response.data.data);
            } else {
                toast.error('Failed to load purchase details', { autoClose: 1500 });
                setShowDetailsModal(false);
            }
        } catch (error) {
            console.error('Failed to fetch purchase details:', error);
            toast.error('Failed to load purchase details', { autoClose: 1500 });
            setShowDetailsModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    const formatDateString = (value) => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return value;
        }
    };

    const formatExpiryDate = (value) => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            return `${month}/${year}`;
        } catch {
            return value;
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(purchaseData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = purchaseData.slice(startIndex, endIndex);

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate, filters.reportType, filters.paymentMode, filters.billDateType, filters.invoiceNumber, selectedSupplier, purchaseData.length]);

    const getPurchaseReport = useCallback(async () => {
        // Validate supplier selection for Supplier Wise report
        if (filters.reportType === 'Supplier' && !selectedSupplier) {
            return;
        }

        if (filters.reportType === 'Invoice') {
            const invoiceNo = String(filters.invoiceNumber ?? '').trim();
            if (!invoiceNo) {
                return;
            }

            setLoading(true);
            try {
                const response = await axios.get(`${BASE_URL}${GET_PURCHASE_DETAILS_BY_INVOICE_NO}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`,
                        'invoiceNo': invoiceNo,
                        'sourceDocument': 'Pharmacy',
                    },
                });

                if (response.status === 200 && response.data?.isSucess) {
                    const purchaseHd = response.data?.data?.purchaseHd || [];
                    const mappedData = purchaseHd.map((item) => mapPurchaseHdToRow(item, invoiceNo));
                    setPurchaseData(mappedData);
                    if (mappedData.length === 0) {
                        toast.info('No purchase found for this invoice number', { autoClose: 2000 });
                    }
                } else {
                    toast.error(response.data?.message || 'Failed to fetch purchase details', { autoClose: 2000 });
                    setPurchaseData([]);
                }
            } catch (error) {
                console.error('Failed to fetch purchase by invoice number:', error);
                toast.error('Failed to fetch purchase details', { autoClose: 2000 });
                setPurchaseData([]);
            } finally {
                setLoading(false);
            }
            return;
        }

        setLoading(true);
        console.log('Filters:', filters);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            // rpType: Supplier → 'Supplier'; Bill → selected date type or default 'Bill'
            const rpType =
                filters.reportType === 'Supplier'
                    ? 'Supplier'
                    : filters.billDateType || 'Bill';
            let searchKey = 0;

            if (filters.reportType === 'Supplier' && selectedSupplier) {
                searchKey = selectedSupplier.SupplierID ?? selectedSupplier.supplierID ?? selectedSupplier.Id ?? selectedSupplier.id ?? selectedSupplier.SupplierId ?? selectedSupplier.value ?? 0;
            }
            
            const response = await axios.get(`${BASE_URL}${GET_PURCHASE_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': rpType,
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'searchKey': searchKey,
                    'itemNo': 0,
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by numeric InvoiceNo ascending (internal bill order)
                const sortedData = [...data].sort((a, b) => {
                    const numA = Number(a.InvoiceNo);
                    const numB = Number(b.InvoiceNo);
                    const safeA = Number.isFinite(numA) ? numA : 0;
                    const safeB = Number.isFinite(numB) ? numB : 0;
                    return safeA - safeB;
                });
                setPurchaseData(sortedData);
                console.log('Purchase report:', sortedData);
            } else {
                toast.error('Failed to fetch purchase report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch purchase report:', error);
            toast.error('Failed to fetch purchase report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token, selectedSupplier]);

    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Purchase Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        {getReportTitle()}
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.invoiceOnly && filters.reportType !== 'Invoice') {
                                        return null;
                                    }

                                    if (filter.type === 'text') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="text"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    placeholder="Enter reference number"
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'date') {
                                        if (filters.reportType === 'Invoice') {
                                            return null;
                                        }

                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'select') {
                                        if (filter.billWiseOnly && filters.reportType !== 'Bill') {
                                            return null;
                                        }

                                        if (filter.id === 'paymentMode' && filters.reportType === 'Invoice') {
                                            return null;
                                        }

                                        const isBillDateType = filter.id === 'billDateType';
                                        const isDisabled = isBillDateType && filters.reportType !== 'Bill';

                                        return (
                                            <label
                                                key={filter.id}
                                                className={`flex flex-col gap-1 text-xs font-medium text-slate-700 ${
                                                    isBillDateType ? 'shrink-0' : ''
                                                }`}
                                            >
                                                {filter.label}
                                                <select
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    disabled={isDisabled}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                                                >
                                                    {filter.emptyDefault && (
                                                        <option value="">—</option>
                                                    )}
                                                    {(filter.options ?? []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }

                                    return null;
                                })}

                                {filters.reportType === 'Supplier' && (
                                    <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                        Supplier
                                        <div className="[&>div]:!h-auto">
                                            <AsyncSelect
                                                loadOptions={loadSupplierOptions}
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                defaultOptions={true}
                                                cacheOptions={true}
                                                value={getSelectedSupplierOption(selectedSupplier?.value)}
                                                onChange={handleSupplierChange}
                                                noOptionsMessage={({ inputValue }) =>
                                                    inputValue && inputValue.length < 2
                                                        ? 'Type at least 2 characters to search'
                                                        : 'No suppliers found'
                                                }
                                                loadingMessage={() => 'Searching suppliers...'}
                                                styles={customSelectStyles}
                                                placeholder="Select Supplier"
                                            />
                                        </div>
                                    </label>
                                )}
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                disabled={
                                    (filters.reportType === 'Supplier' && !selectedSupplier) ||
                                    (filters.reportType === 'Invoice' && !String(filters.invoiceNumber ?? '').trim())
                                }
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setSelectedSupplier(null);
                                    setPurchaseData([]);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-6 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-3">
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">{purchaseData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{purchaseData.reduce((sum, item) => sum + (parseFloat(item.NetAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Tax
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{purchaseData.reduce((sum, item) => sum + (parseFloat(item.TaxAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-1">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, purchaseData.length)}</span> of <span className="font-semibold text-slate-700">{purchaseData.length}</span> datas for this report.
                                    </p>
                                    {/* Items per page selector */}
                                    {/* <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div> */}
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                            <th
                                                scope="col"
                                                className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500"
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {purchaseData.length > 0 ? (
                                            paginatedData.map((row, index) => {
                                                const canEditRow = isPurchaseRowInCurrentFinYear(row);
                                                return (
                                                <tr key={`${row.InvoiceNo}-${row.Sequence}-${index}`} className="hover:bg-indigo-50/40 transition">
                                                    {reportConfig.table.columns.map((column) => {
                                                        let cellValue = row[column.key];
                                                        
                                                        // Format specific columns
                                                        if (column.key === 'InvoiceNo') {
                                                            cellValue = formatInvoiceNoWithSequence(row);
                                                        } else if (column.key === 'PurchaseDate') {
                                                            cellValue = new Date(cellValue).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric'
                                                            });
                                                        } else if (['NetAmount', 'TaxAmount', 'DiscountAmount', 'BalanceDue'].includes(column.key)) {
                                                            cellValue = `₹${parseFloat(cellValue || 0).toFixed(2)}`;
                                                        } else if (column.key === 'PaymentMode') {
                                                            // Capitalize first letter of payment mode
                                                            cellValue = cellValue ? cellValue.charAt(0).toUpperCase() + cellValue.slice(1) : '-';
                                                        } else if (column.key === 'SupplierName' && (!cellValue || cellValue === null)) {
                                                            cellValue = '-';
                                                        }
                                                        
                                                        return (
                                                            <td key={column.key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                                {cellValue}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => handleViewDetails(row)}
                                                                className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="View Details"
                                                            >
                                                                <FiEye size={18} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEdit(row)}
                                                                className='inline-flex items-center justify-center p-2 rounded-lg transition-colors text-indigo-600 hover:bg-indigo-50
                                                                        text-slate-300'
                                                            >
                                                                <FiEdit2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                                                    {filters.reportType === 'Supplier' && !selectedSupplier
                                                        ? 'Please select a supplier to view purchase reports'
                                                        : filters.reportType === 'Invoice' && !String(filters.invoiceNumber ?? '').trim()
                                                        ? 'Enter an invoice number and click Generate'
                                                        : filters.reportType === 'Invoice'
                                                        ? 'No purchase found for this invoice number'
                                                        : 'No data available for the selected period'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Bottom Pagination Controls */}
                            {purchaseData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>


            {/* Purchase Details Modal */}
            {showDetailsModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl w-[95%] max-w-6xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-slate-800">Purchase Invoice Details</h2>
                            <button
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    setPurchaseDetails(null);
                                }}
                                className="p-2 text-black hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingDetails ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-600"></div>
                                </div>
                            ) : purchaseDetails ? (
                                <div className="space-y-6">
                                    {/* Header Information */}
                                    {purchaseDetails.invHeader?.[0] && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Purchase Information</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Supplier</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].SupplierName || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Invoice Number</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].PurchaseRefNo || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Ref No / Sequence</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">
                                                        {formatInvoiceNoWithSequence(purchaseDetails.invHeader[0])}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Entry Date</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{formatDateString(purchaseDetails.invHeader[0].InvoiceDate || purchaseDetails.invHeader[0].PurchaseDate)}</p>
                                                </div>

                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Bill Date</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{formatDateString(purchaseDetails.invHeader[0].PurchaseDate || purchaseDetails.invHeader[0].PurchaseDate)}</p>
                                                </div>

                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Payment Mode</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">{purchaseDetails.invHeader[0].PaymentMode || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Due Date</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{formatDateString(purchaseDetails.invHeader[0].DueDate) || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Remarks</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].Remarks || '-'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Line Items */}
                                    {purchaseDetails.invLineDetail && purchaseDetails.invLineDetail.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Product Details</h3>
                                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">No.</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Product Name</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Batch</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Packing</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Qty</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Free</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Rate</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">MRP</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Amount</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Discount</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">GST%</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Tax</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {purchaseDetails.invLineDetail.map((line, index) => {
                                                            const batchDetail = purchaseDetails.invBatchDetails?.find(
                                                                (b) => b.Item_No === line.ItemNo && b.BatchCode === line.BatchCode
                                                            );
                                                            const packageSize = line.PackageSize || line.Packing || 1;
                                                            const totalUnits = line.Quantity || 0;
                                                            const freeUnits = line.FOC || 0;
                                                            const paidUnits = totalUnits - freeUnits;
                                                            const qty = packageSize > 0 ? paidUnits / packageSize : 0;
                                                            const foc = packageSize > 0 ? freeUnits / packageSize : 0;
                                                            const unitRate = line.Price || 0;
                                                            const rate = unitRate * packageSize;
                                                            const unitMrp = line.MRP || 0;
                                                            const mrp = unitMrp * packageSize;

                                                            return (
                                                                <tr key={index} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3 text-black">{index + 1}</td>
                                                                    <td className="px-4 py-3 font-medium text-black">{line.ItemName || '-'}</td>
                                                                    <td className="px-4 py-3 text-black">{line.BatchCode || '-'}</td>
                                                                    <td className="px-4 py-3 text-black">{packageSize}</td>
                                                                    <td className="px-4 py-3 text-right text-black">{qty.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">{foc.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{rate.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{mrp.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{parseFloat(line.Total || 0).toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">
                                                                        {line.DiscountPercentage ? `${line.DiscountPercentage}%` : `₹${parseFloat(line.DiscountAmount || 0).toFixed(2)}`}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right text-black">{line.TaxCode || '0'}%</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{parseFloat(line.TaxAmount || 0).toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right font-semibold text-black">₹{parseFloat(line.NetTotal || 0).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}


                                    {/* Summary */}
                                    {purchaseDetails.invHeader?.[0] && (
                                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Summary</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Sub Total</p>
                                                    <p className="text-lg font-bold text-slate-800 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].Total || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Discount</p>
                                                    <p className="text-lg font-bold text-red-600 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].DiscountAmount || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Tax Amount</p>
                                                    <p className="text-lg font-bold text-slate-800 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].TaxAmount || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Grand Total</p>
                                                    <p className="text-xl font-bold text-indigo-600 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].NetAmount || 0).toFixed(2)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-black">No details available</div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end p-4 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    setPurchaseDetails(null);
                                }}
                                className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default withAuth(PurchaseBillWise);